import React, { useState, useEffect } from 'react';
import './TankDashboard.css';

function TankDashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(10000); // 10 seconds default for real-time updates
  const [selectedTank, setSelectedTank] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [tankHistory, setTankHistory] = useState([]);

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      const response = await fetch('/api/tanks/dashboard');
      if (!response.ok) throw new Error('Failed to fetch tank dashboard data');
      const data = await response.json();
      setDashboardData(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch tank transaction history
  const fetchTankHistory = async (tankId) => {
    try {
      const response = await fetch(`/api/tanks/${tankId}/history?limit=20`);
      if (!response.ok) throw new Error('Failed to fetch tank history');
      const data = await response.json();
      setTankHistory(data);
    } catch (err) {
      console.error('Error fetching tank history:', err);
    }
  };

  // Initial load and set up refresh interval
  useEffect(() => {
    fetchDashboardData();

    // Set up auto-refresh
    const interval = setInterval(() => {
      fetchDashboardData();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [refreshInterval]);

  // Handle tank click to view details
  const handleTankClick = (tank) => {
    setSelectedTank(tank);
    setShowHistory(true);
    fetchTankHistory(tank.id);
  };

  // Close tank details modal
  const closeModal = () => {
    setShowHistory(false);
    setSelectedTank(null);
    setTankHistory([]);
  };

  // Get tank status badge
  const getTankStatus = (tank) => {
    if (!tank.blend_id) return 'empty';
    if (tank.current_level_lbs < 10) return 'low';
    if (tank.utilizationPercent > 90) return 'full';
    return 'active';
  };

  // Get status color class
  const getStatusColorClass = (status) => {
    switch (status) {
      case 'empty': return 'status-empty';
      case 'low': return 'status-low';
      case 'active': return 'status-active';
      case 'full': return 'status-full';
      default: return 'status-empty';
    }
  };

  // Format date/time
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !dashboardData) {
    return (
      <div className="tank-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading tank dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !dashboardData) {
    return (
      <div className="tank-dashboard">
        <div className="error-container">
          <h2>Error Loading Dashboard</h2>
          <p>{error}</p>
          <button onClick={fetchDashboardData} className="retry-button">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="tank-dashboard">
      <div className="dashboard-header">
        <h1>Tank & Melter Dashboard</h1>
        <div className="header-actions">
          <div className="refresh-controls">
            <label>Auto-refresh:</label>
            <select
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(parseInt(e.target.value))}
              className="refresh-select"
            >
              <option value={5000}>5 seconds</option>
              <option value={10000}>10 seconds</option>
              <option value={30000}>30 seconds</option>
              <option value={60000}>1 minute</option>
            </select>
          </div>
          <button onClick={fetchDashboardData} className="refresh-button">
            Refresh Now
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {dashboardData?.summary && (
        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-label">Tanks in Use</div>
            <div className="summary-value">
              {dashboardData.summary.tanksInUse} / {dashboardData.summary.totalTanks}
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Total Capacity</div>
            <div className="summary-value">
              {dashboardData.summary.totalCapacityLbs.toFixed(0)} lbs
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Current Wax</div>
            <div className="summary-value">
              {dashboardData.summary.totalCurrentLbs.toFixed(0)} lbs
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Active Blends</div>
            <div className="summary-value">
              {dashboardData.summary.activeBlends}
            </div>
          </div>
        </div>
      )}

      {/* Tanks Grid */}
      <div className="tanks-section">
        <h2>Tanks</h2>
        <div className="tanks-grid">
          {dashboardData?.tanks?.map((tank) => {
            const status = getTankStatus(tank);
            return (
              <div
                key={tank.id}
                className={`tank-card ${getStatusColorClass(status)}`}
                onClick={() => handleTankClick(tank)}
              >
                <div className="tank-header">
                  <h3>{tank.tank_name}</h3>
                  <span className={`status-badge ${status}`}>
                    {status.toUpperCase()}
                  </span>
                </div>

                <div className="tank-body">
                  {/* Level indicator */}
                  <div className="level-indicator">
                    <div className="level-bar-container">
                      <div
                        className="level-bar"
                        style={{ height: `${tank.utilizationPercent}%` }}
                      >
                        <span className="level-text">
                          {tank.utilizationPercent}%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Tank info */}
                  <div className="tank-info">
                    <div className="info-row">
                      <span className="label">Current:</span>
                      <span className="value">{parseFloat(tank.current_level_lbs).toFixed(1)} lbs</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Capacity:</span>
                      <span className="value">{parseFloat(tank.capacity_lbs).toFixed(0)} lbs</span>
                    </div>

                    {tank.fragrance_oil_name && (
                      <div className="info-row fragrance">
                        <span className="label">Fragrance:</span>
                        <span className="value">{tank.fragrance_oil_name}</span>
                      </div>
                    )}

                    {tank.blend && (
                      <div className="blend-info">
                        <div className="blend-name">{tank.blend.blend_name}</div>
                        {tank.blend.blend_date && (
                          <div className="blend-date">
                            Blended: {new Date(tank.blend.blend_date).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    )}

                    {tank.last_filled_at && (
                      <div className="info-row timestamp">
                        <span className="label">Last Filled:</span>
                        <span className="value">{formatDateTime(tank.last_filled_at)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Melters Grid */}
      <div className="melters-section">
        <h2>Melters</h2>
        <div className="melters-grid">
          {dashboardData?.melters?.map((melter) => {
            const hasActiveBlends = melter.activeBlends && melter.activeBlends.length > 0;
            return (
              <div
                key={melter.id}
                className={`melter-card ${hasActiveBlends ? 'active' : 'idle'}`}
              >
                <div className="melter-header">
                  <h3>{melter.name}</h3>
                  <span className={`melter-status ${hasActiveBlends ? 'in-use' : 'idle'}`}>
                    {hasActiveBlends ? 'IN USE' : 'IDLE'}
                  </span>
                </div>

                <div className="melter-body">
                  <div className="melter-specs">
                    <div className="spec-item">
                      <span className="label">Capacity:</span>
                      <span className="value">{parseFloat(melter.capacity_lbs).toFixed(0)} lbs</span>
                    </div>
                    <div className="spec-item">
                      <span className="label">Lbs/Inch:</span>
                      <span className="value">{parseFloat(melter.lbs_per_inch).toFixed(2)}</span>
                    </div>
                  </div>

                  {hasActiveBlends && (
                    <div className="active-blends">
                      <h4>Active Blends:</h4>
                      {melter.activeBlends.map((blend) => (
                        <div key={blend.id} className="blend-item">
                          <div className="blend-name">{blend.blend_name}</div>
                          <div className="blend-details">
                            <span>{parseFloat(blend.total_wax_lbs).toFixed(1)} lbs wax</span>
                            {blend.wax_inches && (
                              <span> • {parseFloat(blend.wax_inches).toFixed(1)}"</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tank History Modal */}
      {showHistory && selectedTank && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedTank.tank_name} - Transaction History</h2>
              <button onClick={closeModal} className="close-button">&times;</button>
            </div>
            <div className="modal-body">
              <div className="tank-current-status">
                <div className="status-item">
                  <span className="label">Current Level:</span>
                  <span className="value">{parseFloat(selectedTank.current_level_lbs).toFixed(1)} lbs</span>
                </div>
                <div className="status-item">
                  <span className="label">Capacity:</span>
                  <span className="value">{parseFloat(selectedTank.capacity_lbs).toFixed(0)} lbs</span>
                </div>
                <div className="status-item">
                  <span className="label">Utilization:</span>
                  <span className="value">{selectedTank.utilizationPercent}%</span>
                </div>
              </div>

              {tankHistory.length > 0 ? (
                <div className="history-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Date/Time</th>
                        <th>Type</th>
                        <th>Amount (lbs)</th>
                        <th>Operator</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tankHistory.map((transaction) => (
                        <tr key={transaction.id}>
                          <td>{formatDateTime(transaction.created_at)}</td>
                          <td>
                            <span className={`transaction-type ${transaction.transaction_type}`}>
                              {transaction.transaction_type.toUpperCase()}
                            </span>
                          </td>
                          <td className="amount">
                            {transaction.transaction_type === 'fill' ? '+' : '-'}
                            {parseFloat(transaction.amount_lbs).toFixed(1)}
                          </td>
                          <td>{transaction.operator_name || 'System'}</td>
                          <td className="notes">{transaction.notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-history">
                  <p>No transaction history available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TankDashboard;
